import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { 
  FileTextIcon, 
  DownloadIcon, 
  CalendarIcon,
  BarChart3Icon,
  TrendingUpIcon,
  ClockIcon
} from "lucide-react";
import { format } from "date-fns";
import { ptBR, enUS, es as esLocale } from "date-fns/locale";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";

const dateFnsLocales: Record<string, typeof ptBR> = { pt: ptBR, en: enUS, es: esLocale };

const Reports = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, locale } = useLanguage();
  const dateLocale = dateFnsLocales[locale] || ptBR;
  const [user, setUser] = useState<any>(null);
  const [reportType, setReportType] = useState("summary");
  const [startDate, setStartDate] = useState<Date | undefined>(new Date(new Date().setMonth(new Date().getMonth() - 1)));
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const userData = getUser();
    if (!userData) {
      navigate("/login");
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);

    // Apenas internos e admins podem acessar relatórios
    if (parsedUser.role !== "internal" && parsedUser.role !== "admin") {
      navigate("/chamados");
      return;
    }
  }, [navigate]);

  const generateReport = async () => {
    if (!startDate || !endDate) {
      toast({
        title: t.reports.error,
        description: t.reports.selectDates,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      
      // Formatar datas para o backend
      const startDateStr = format(startDate, "yyyy-MM-dd");
      const endDateStr = format(endDate, "yyyy-MM-dd");
      
      const response = await fetch(
        getApiEndpoint(`/api/tickets/reports/generate?startDate=${startDateStr}&endDate=${endDateStr}&reportType=${reportType}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(t.reports.errorGenerating);
      }

      const data = await response.json();
      
      // Formatar período para exibição
      const formattedData = {
        ...data,
        period: {
          start: format(new Date(startDateStr), "dd/MM/yyyy", { locale: dateLocale }),
          end: format(new Date(endDateStr), "dd/MM/yyyy", { locale: dateLocale })
        }
      };

      setReportData(formattedData);
      toast({
        title: t.reports.reportGenerated,
        description: t.reports.reportGeneratedDesc,
      });
    } catch (error: any) {
      toast({
        title: t.reports.error,
        description: error.message || t.reports.errorGenerating,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const exportReport = (reportFormat: 'pdf' | 'csv' | 'excel') => {
    if (!reportData) {
      toast({
        title: t.reports.error,
        description: t.reports.generateFirst,
        variant: "destructive",
      });
      return;
    }

    try {
      // Garantir que os dados existam e nenhum valor seja undefined na exportação
      const rawSummary = reportData.summary || {};
      const data = {
        period: reportData.period || { start: '', end: '' },
        summary: {
          total: rawSummary.total ?? 0,
          open: rawSummary.open ?? 0,
          backlog: rawSummary.backlog ?? rawSummary.open ?? 0,
          backlog_overdue: rawSummary.backlog_overdue ?? 0,
          backlog_sla_breached: rawSummary.backlog_sla_breached ?? 0,
          backlog_transferred: rawSummary.backlog_transferred ?? 0,
          resolved: rawSummary.resolved ?? 0,
          closed: rawSummary.closed ?? 0,
          average_resolution_time: rawSummary.average_resolution_time ?? '0h',
          average_response_time: rawSummary.average_response_time ?? '0h',
          sla_compliance: rawSummary.sla_compliance ?? 0,
          first_resolution_rate: rawSummary.first_resolution_rate ?? 0
        },
        byCategory: reportData.byCategory || reportData.topCategories || {},
        byTechnician: reportData.byTechnician || reportData.topTechnicians || {},
        backlogByAge: reportData.backlogByAge || {},
        transfers: reportData.transfers || {
          total: 0,
          backlog: 0,
          multiple_transfers: 0
        },
        reopenRate: reportData.reopenRate ?? 0,
        npsAverage: reportData.npsAverage ?? 0,
        slaBreaches: reportData.slaBreaches ?? 0
      };
      if (reportFormat === 'csv') {
        // Exportar CSV
        const csvRows: string[] = [];
        
        // Cabeçalho
        csvRows.push(t.reports.reportTitle);
        csvRows.push(`${t.reports.periodLabel}: ${data.period.start || ''} a ${data.period.end || ''}`);
        csvRows.push('');
        
        // Resumo
        csvRows.push(t.reports.summary);
        csvRows.push(`${t.reports.totalTickets},${data.summary.total}`);
        csvRows.push(`${t.reports.backlog},${data.summary.backlog || data.summary.open}`);
        csvRows.push(`${t.reports.backlogOverdue},${data.summary.backlog_overdue || 0}`);
        csvRows.push(`${t.reports.backlogSlaBreached},${data.summary.backlog_sla_breached || 0}`);
        csvRows.push(`${t.reports.backlogTransferred},${data.summary.backlog_transferred || 0}`);
        csvRows.push(`${t.reports.totalTransferred},${data.transfers?.total || 0}`);
        csvRows.push(`${t.reports.multipleTransfers},${data.transfers?.multiple_transfers || 0}`);
        csvRows.push(`${t.reports.open},${data.summary.open}`);
        csvRows.push(`${t.reports.resolved},${data.summary.resolved}`);
        csvRows.push(`${t.reports.closed},${data.summary.closed}`);
        csvRows.push(`${t.reports.averageResolutionTime},${data.summary.average_resolution_time || '0h'}`);
        csvRows.push(`${t.reports.averageResponseTime},${data.summary.average_response_time || '0h'}`);
        csvRows.push(`${t.reports.slaCompliance},${data.summary.sla_compliance || 0}%`);
        csvRows.push(`${t.reports.firstResolution},${data.summary.first_resolution_rate || 0}%`);
        csvRows.push(`${t.reports.reopenRate},${data.reopenRate || 0}%`);
        csvRows.push(`${t.reports.npsAverage},${data.npsAverage || 0}`);
        csvRows.push('');
        
        // Por Categoria
        csvRows.push(t.reports.ticketsByCategory);
        csvRows.push(`${t.reports.category},${t.reports.quantity}`);
        Object.entries(data.byCategory).forEach(([category, count]) => {
          csvRows.push(`"${category}",${count}`);
        });
        csvRows.push('');
        
        // Por Técnico
        if (Object.keys(data.byTechnician).length > 0) {
          csvRows.push(t.reports.ticketsByTechnician);
          csvRows.push(`${t.reports.technician},${t.reports.quantity}`);
          Object.entries(data.byTechnician).forEach(([technician, count]) => {
            csvRows.push(`"${technician}",${count}`);
          });
        }
        
        const csvContent = csvRows.join('\n');
        // Adicionar BOM para UTF-8
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio_${format(new Date(), 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast({
          title: t.reports.exportSuccess,
          description: t.reports.exportCsvDesc,
        });
      } else if (reportFormat === 'excel') {
        // Para Excel, vamos gerar um CSV que pode ser aberto no Excel
        const csvRows: string[] = [];
        
        csvRows.push(t.reports.reportTitle);
        csvRows.push(`${t.reports.periodLabel}: ${data.period.start || ''} a ${data.period.end || ''}`);
        csvRows.push('');
        csvRows.push(t.reports.summary);
        csvRows.push(`${t.reports.metric},${t.reports.value}`);
        csvRows.push(`${t.reports.totalTickets},${data.summary.total}`);
        csvRows.push(`${t.reports.backlog},${data.summary.backlog || data.summary.open}`);
        csvRows.push(`${t.reports.backlogOverdue},${data.summary.backlog_overdue || 0}`);
        csvRows.push(`${t.reports.backlogSlaBreached},${data.summary.backlog_sla_breached || 0}`);
        csvRows.push(`${t.reports.backlogTransferred},${data.summary.backlog_transferred || 0}`);
        csvRows.push(`${t.reports.totalTransferred},${data.transfers?.total || 0}`);
        csvRows.push(`${t.reports.multipleTransfers},${data.transfers?.multiple_transfers || 0}`);
        csvRows.push(`${t.reports.open},${data.summary.open}`);
        csvRows.push(`${t.reports.resolved},${data.summary.resolved}`);
        csvRows.push(`${t.reports.closed},${data.summary.closed}`);
        csvRows.push(`${t.reports.averageResolutionTime},${data.summary.average_resolution_time || '0h'}`);
        csvRows.push(`${t.reports.averageResponseTime},${data.summary.average_response_time || '0h'}`);
        csvRows.push(`${t.reports.slaCompliance},${data.summary.sla_compliance || 0}%`);
        csvRows.push(`${t.reports.firstResolution},${data.summary.first_resolution_rate || 0}%`);
        csvRows.push(`${t.reports.reopenRate},${data.reopenRate || 0}%`);
        csvRows.push(`${t.reports.npsAverage},${data.npsAverage || 0}`);
        csvRows.push('');
        csvRows.push(t.reports.ticketsByCategory);
        csvRows.push(`${t.reports.category},${t.reports.quantity}`);
        Object.entries(data.byCategory).forEach(([category, count]) => {
          csvRows.push(`"${category}",${count}`);
        });
        csvRows.push('');
        if (Object.keys(data.byTechnician).length > 0) {
          csvRows.push(t.reports.ticketsByTechnician);
          csvRows.push(`${t.reports.technician},${t.reports.quantity}`);
          Object.entries(data.byTechnician).forEach(([technician, count]) => {
            csvRows.push(`"${technician}",${count}`);
          });
          csvRows.push('');
        }
        
        // Backlog por Idade
        if (Object.keys(data.backlogByAge || {}).length > 0) {
          csvRows.push('Backlog por Idade');
          csvRows.push('Idade,Quantidade');
          Object.entries(data.backlogByAge).forEach(([age, count]) => {
            csvRows.push(`"${age}",${count}`);
          });
        }
        
        const csvContent = csvRows.join('\n');
        // Adicionar BOM para UTF-8
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio_${format(new Date(), 'yyyy-MM-dd')}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast({
          title: t.reports.exportSuccess,
          description: t.reports.exportExcelDesc,
        });
      } else if (reportFormat === 'pdf') {
        // Para PDF, vamos criar um HTML e usar window.print()
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          toast({
            title: t.reports.error,
            description: t.reports.allowPopups,
            variant: "destructive",
          });
          return;
        }
        
        // Escapar caracteres especiais para HTML
        const escapeHtml = (text: string) => {
          const div = document.createElement('div');
          div.textContent = text;
          return div.innerHTML;
        };
        // Garantir que valor nunca seja exibido como "undefined"
        const fmt = (v: unknown): string => {
          if (v === undefined || v === null) return '—';
          return String(v);
        };
        
        const htmlContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>Relatório de Chamados</title>
              <meta charset="utf-8">
              <style>
                * { box-sizing: border-box; }
                body { font-family: 'Segoe UI', system-ui, sans-serif; padding: 24px; max-width: 900px; margin: 0 auto; color: #1f2937; line-height: 1.5; }
                .header { border-bottom: 2px solid #2563eb; padding-bottom: 16px; margin-bottom: 24px; }
                .header h1 { margin: 0 0 8px 0; font-size: 1.75rem; color: #1e40af; }
                .header .period { font-size: 0.95rem; color: #4b5563; }
                h2 { font-size: 1.15rem; color: #374151; margin: 24px 0 12px 0; padding-bottom: 4px; }
                table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 0.9rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
                th, td { border: 1px solid #e5e7eb; padding: 10px 12px; text-align: left; }
                th { background: #f8fafc; font-weight: 600; color: #374151; }
                tr:nth-child(even) { background: #f9fafb; }
                tr:hover { background: #f3f4f6; }
                .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 0.8rem; color: #6b7280; }
                @media print {
                  body { padding: 12px; }
                  .header { border-color: #1e40af; }
                  table { box-shadow: none; }
                  tr:hover { background: inherit; }
                  @page { margin: 1.5cm; size: A4; }
                }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>${escapeHtml(t.reports.reportTitle)}</h1>
                <p class="period"><strong>${escapeHtml(t.reports.periodLabel)}:</strong> ${escapeHtml(fmt(data.period?.start))} a ${escapeHtml(fmt(data.period?.end))}</p>
              </div>
              
              <h2>${escapeHtml(t.reports.summary)}</h2>
              <table>
                <tr><th>${escapeHtml(t.reports.metric)}</th><th>${escapeHtml(t.reports.value)}</th></tr>
                <tr><td>${escapeHtml(t.reports.totalTickets)}</td><td>${fmt(data.summary.total)}</td></tr>
                <tr><td>${escapeHtml(t.reports.backlog)}</td><td>${fmt(data.summary.backlog)}</td></tr>
                <tr><td>${escapeHtml(t.reports.backlogOverdue)}</td><td>${fmt(data.summary.backlog_overdue)}</td></tr>
                <tr><td>${escapeHtml(t.reports.backlogSlaBreached)}</td><td>${fmt(data.summary.backlog_sla_breached)}</td></tr>
                <tr><td>${escapeHtml(t.reports.backlogTransferred)}</td><td>${fmt(data.summary.backlog_transferred)}</td></tr>
                <tr><td>${escapeHtml(t.reports.totalTransferred)}</td><td>${fmt(data.transfers?.total)}</td></tr>
                <tr><td>${escapeHtml(t.reports.multipleTransfers)}</td><td>${fmt(data.transfers?.multiple_transfers)}</td></tr>
                <tr><td>${escapeHtml(t.reports.open)}</td><td>${fmt(data.summary.open)}</td></tr>
                <tr><td>${escapeHtml(t.reports.resolved)}</td><td>${fmt(data.summary.resolved)}</td></tr>
                <tr><td>${escapeHtml(t.reports.closed)}</td><td>${fmt(data.summary.closed)}</td></tr>
                <tr><td>${escapeHtml(t.reports.averageResolutionTime)}</td><td>${escapeHtml(fmt(data.summary.average_resolution_time))}</td></tr>
                <tr><td>${escapeHtml(t.reports.averageResponseTime)}</td><td>${escapeHtml(fmt(data.summary.average_response_time))}</td></tr>
                <tr><td>${escapeHtml(t.reports.slaCompliance)}</td><td>${fmt(data.summary.sla_compliance)}%</td></tr>
                <tr><td>${escapeHtml(t.reports.firstResolution)}</td><td>${fmt(data.summary.first_resolution_rate)}%</td></tr>
                <tr><td>${escapeHtml(t.reports.reopenRate)}</td><td>${fmt(data.reopenRate)}%</td></tr>
                <tr><td>${escapeHtml(t.reports.npsAverage)}</td><td>${fmt(data.npsAverage)}</td></tr>
              </table>
              
              <h2>${escapeHtml(t.reports.ticketsByCategory)}</h2>
              <table>
                <tr><th>${escapeHtml(t.reports.category)}</th><th>${escapeHtml(t.reports.quantity)}</th></tr>
                ${Object.entries(data.byCategory).map(([category, count]) => 
                  `<tr><td>${escapeHtml(category)}</td><td>${fmt(count)}</td></tr>`
                ).join('')}
                ${Object.keys(data.byCategory).length === 0 ? `<tr><td colspan="2">${escapeHtml(t.reports.noDataInPeriod)}</td></tr>` : ''}
              </table>
              
              ${Object.keys(data.byTechnician).length > 0 ? `
              <h2>${escapeHtml(t.reports.ticketsByTechnician)}</h2>
              <table>
                <tr><th>${escapeHtml(t.reports.technician)}</th><th>${escapeHtml(t.reports.quantity)}</th></tr>
                ${Object.entries(data.byTechnician).map(([technician, count]) => 
                  `<tr><td>${escapeHtml(technician)}</td><td>${fmt(count)}</td></tr>`
                ).join('')}
              </table>
              ` : ''}
              
              ${Object.keys(data.backlogByAge || {}).length > 0 ? `
              <h2>${escapeHtml(t.reports.backlogByAge)}</h2>
              <table>
                <tr><th>${escapeHtml(t.reports.age)}</th><th>${escapeHtml(t.reports.quantity)}</th></tr>
                ${Object.entries(data.backlogByAge).map(([age, count]) => 
                  `<tr><td>${escapeHtml(age)}</td><td>${fmt(count)}</td></tr>`
                ).join('')}
              </table>
              ` : ''}
              
              <div class="footer">
                ${t.reports.generatedAt} ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: dateLocale })} — TEC-IT ITSM
              </div>
            </body>
          </html>
        `;
        
        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        // Aguardar o conteúdo carregar antes de imprimir
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
        
        toast({
          title: t.reports.openingPrint,
          description: t.reports.openingPrintDesc,
        });
      }
    } catch (error: any) {
      console.error('Erro ao exportar:', error);
      toast({
        title: t.reports.error,
        description: error.message || t.reports.errorGenerating,
        variant: "destructive",
      });
    }
  };

  // Calcular máximo para barras de progresso
  const getMaxCategoryCount = () => {
    if (!reportData?.byCategory) return 1;
    return Math.max(...Object.values(reportData.byCategory) as number[]);
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.reports.title}</h1>
          <p className="text-gray-600">
            {t.reports.subtitleAlt}
          </p>
        </div>

        {/* Configuração do Relatório */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t.reports.configureReport}</CardTitle>
            <CardDescription>
              {t.reports.configureDesc}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>{t.reports.reportType}</Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="summary">{t.reports.summaryExecutive}</SelectItem>
                    <SelectItem value="detailed">{t.reports.detailed}</SelectItem>
                    <SelectItem value="sla">{t.reports.slaAnalysis}</SelectItem>
                    <SelectItem value="technician">{t.reports.byTechnician}</SelectItem>
                    <SelectItem value="category">{t.reports.byCategory}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t.reports.period}</Label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "dd/MM/yyyy", { locale: dateLocale }) : t.reports.startDate}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "dd/MM/yyyy", { locale: dateLocale }) : t.reports.endDate}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={generateReport} disabled={loading}>
                {loading ? t.reports.generating : t.reports.generateReport}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Resultado do Relatório */}
        {reportData && (
          <div className="space-y-6">
            {/* Renderização baseada no tipo de relatório */}
            {reportData.reportType === 'sla' ? (
              // Relatório de Análise de SLA
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <TrendingUpIcon className="h-5 w-5 mr-2" />
                      {t.reports.slaAnalysis}
                    </CardTitle>
                    <CardDescription>
                      {reportData.period.start} a {reportData.period.end}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t.reports.slaComplianceShort}</p>
                        <p className="text-2xl font-bold text-green-600">{reportData.summary.sla_compliance}%</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {t.reports.metOfTotal(reportData.summary.sla_met || 0, reportData.summary.sla_total || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t.reports.violations}</p>
                        <p className="text-2xl font-bold text-red-600">{reportData.summary.sla_breaches}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t.reports.backlogOverdue}</p>
                        <p className="text-2xl font-bold text-orange-600">{reportData.summary.backlog_overdue}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{t.reports.averageResponseTime}</p>
                        <p className="text-2xl font-bold">{reportData.summary.average_response_time || '0h'}</p>
                      </div>
                    </div>

                    {reportData.slaDetails?.byCategory && Object.keys(reportData.slaDetails.byCategory).length > 0 && (
                      <div className="mt-6 pt-6 border-t">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">{t.reports.slaByCategory}</h3>
                        <div className="space-y-3">
                          {Object.entries(reportData.slaDetails.byCategory).map(([cat, data]: [string, any]) => (
                            <div key={cat} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">{cat}</span>
                                <Badge variant="outline" className={parseFloat(data.compliance) >= 80 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>
                                  {data.compliance}%
                                </Badge>
                              </div>
                              <div className="text-xs text-gray-500">
                                {t.reports.metOfTotalDetail(data.met || 0, data.with_sla || 0, data.total || 0)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {reportData.slaDetails?.overdue && reportData.slaDetails.overdue.length > 0 && (
                      <div className="mt-6 pt-6 border-t">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">{t.reports.overdueTickets}</h3>
                        <div className="space-y-2">
                          {reportData.slaDetails.overdue.map((ticket: any) => (
                            <div key={ticket.id} className="flex items-center justify-between p-2 bg-red-50 rounded">
                              <div>
                                <span className="text-sm font-medium">#{ticket.id}</span>
                                <span className="text-xs text-gray-500 ml-2">{ticket.category}</span>
                              </div>
                              <Badge variant="outline" className="bg-red-100 text-red-700">
                                {t.reports.daysOverdue(ticket.days_overdue || 0)}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : reportData.reportType === 'technician' ? (
              // Relatório por Técnico
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BarChart3Icon className="h-5 w-5 mr-2" />
                      {t.reports.performanceByTechnician}
                    </CardTitle>
                    <CardDescription>
                      {reportData.period.start} a {reportData.period.end}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {reportData.technicians && reportData.technicians.length > 0 ? (
                      <div className="space-y-4">
                        {reportData.technicians.map((tech: any) => (
                          <Card key={tech.name} className="border-l-4 border-l-blue-500">
                            <CardContent className="pt-4">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold">{tech.name}</h3>
                                <Badge variant="outline">{t.reports.ticketsCount(tech.total)}</Badge>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{t.reports.open}</p>
                                  <p className="text-lg font-bold text-orange-600">{tech.open}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{t.reports.resolved}</p>
                                  <p className="text-lg font-bold text-green-600">{tech.resolved}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{t.reports.averageTime}</p>
                                  <p className="text-lg font-bold">{tech.average_resolution_time}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{t.reports.slaCompliance}</p>
                                  <p className={`text-lg font-bold ${parseFloat(tech.sla_compliance) >= 80 ? 'text-green-600' : 'text-red-600'}`}>
                                    {tech.sla_compliance}%
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-8">{t.reports.noTechnicianFound}</p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : reportData.reportType === 'category' ? (
              // Relatório por Categoria
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BarChart3Icon className="h-5 w-5 mr-2" />
                      {t.reports.categoryAnalysis}
                    </CardTitle>
                    <CardDescription>
                      {reportData.period.start} a {reportData.period.end}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {reportData.categories && reportData.categories.length > 0 ? (
                      <div className="space-y-4">
                        {reportData.categories.map((cat: any) => {
                          const maxTotal = Math.max(...reportData.categories.map((c: any) => c.total));
                          const percentage = maxTotal > 0 ? (cat.total / maxTotal) * 100 : 0;
                          
                          return (
                            <div key={cat.category} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h3 className="text-sm font-semibold text-gray-700">{cat.category}</h3>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{t.reports.percentOfTotal(cat.percentage)}</p>
                                </div>
                                <Badge variant="outline">{t.reports.ticketsCount(cat.total)}</Badge>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div
                                  className="bg-blue-600 h-2.5 rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{t.reports.open}</p>
                                  <p className="font-semibold text-orange-600">{cat.open}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{t.reports.resolved}</p>
                                  <p className="font-semibold text-green-600">{cat.resolved}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{t.reports.averageTime}</p>
                                  <p className="font-semibold">{cat.average_resolution_time}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-8">{t.reports.noCategoryFound}</p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              // Relatório Resumo Executivo ou Detalhado (padrão)
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Chamados por Categoria */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <BarChart3Icon className="h-5 w-5 mr-2" />
                        {t.reports.ticketsByCategory}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries(reportData.byCategory || reportData.topCategories || {}).map(([category, count]: [string, any]) => {
                          const maxCount = getMaxCategoryCount();
                          const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                          
                          return (
                            <div key={category} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">{category}</span>
                                <Badge variant="outline">{count}</Badge>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div
                                  className="bg-blue-600 h-2.5 rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Métricas de SLA */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <TrendingUpIcon className="h-5 w-5 mr-2" />
                        {t.reports.slaMetrics}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.slaCompliance}</span>
                          <span className="text-sm font-medium text-green-600">
                            {reportData.summary.sla_compliance}%
                          </span>
                        </div>
                        {reportData.summary.average_response_time && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.averageResponseTime}</span>
                            <span className="text-sm font-medium">
                              {reportData.summary.average_response_time || '0h'}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.averageResolutionTime}</span>
                          <span className="text-sm font-medium">
                            {reportData.summary.average_resolution_time || '0h'}
                          </span>
                        </div>
                        {reportData.summary.first_resolution_rate !== undefined && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.firstResolution}</span>
                            <span className="text-sm font-medium text-green-600">
                              {reportData.summary.first_resolution_rate || 0}%
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}

            {/* Exportar Relatórios */}
            <Card>
              <CardHeader>
                <CardTitle>{t.reports.exportReports}</CardTitle>
                <CardDescription>
                  {t.reports.exportReportsDesc}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button
                    variant="outline"
                    onClick={() => exportReport('pdf')}
                    className="w-full"
                  >
                    <FileTextIcon className="h-4 w-4 mr-2" />
                    {t.reports.reportMonthlyPdf}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => exportReport('csv')}
                    className="w-full"
                  >
                    <DownloadIcon className="h-4 w-4 mr-2" />
                    {t.reports.performanceCsv}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => exportReport('excel')}
                    className="w-full"
                  >
                    <DownloadIcon className="h-4 w-4 mr-2" />
                    {t.reports.satisfactionExcel}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Resumo Detalhado - apenas para summary e detailed */}
            {(reportData.reportType === 'summary' || reportData.reportType === 'detailed' || !reportData.reportType) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3Icon className="h-5 w-5 mr-2" />
                    {t.reports.periodSummary}
                  </CardTitle>
                  <CardDescription>
                    {reportData.period.start} a {reportData.period.end}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t.reports.totalTickets}</p>
                    <p className="text-2xl font-bold">{reportData.summary.total}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t.reports.backlog}</p>
                    <p className="text-2xl font-bold text-orange-600">{reportData.summary.backlog || reportData.summary.open}</p>
                    {reportData.summary.backlog_overdue > 0 && (
                      <p className="text-xs text-red-600 mt-1">
                        {reportData.summary.backlog_overdue} {t.reports.inOverdue}
                      </p>
                    )}
                    {reportData.summary.backlog_sla_breached > 0 && (
                      <p className="text-xs text-red-600 mt-1">
                        {reportData.summary.backlog_sla_breached} {t.reports.passedSla.split(' ').slice(0, -1).join(' ')}
                      </p>
                    )}
                    {reportData.summary.backlog_transferred > 0 && (
                      <p className="text-xs text-yellow-600 mt-1">
                        {reportData.summary.backlog_transferred} {t.reports.transferredShort}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t.reports.resolved}</p>
                    <p className="text-2xl font-bold text-green-600">{reportData.summary.resolved}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t.reports.closed}</p>
                    <p className="text-2xl font-bold text-gray-600">{reportData.summary.closed}</p>
                  </div>
                </div>
                
                {/* Backlog por Idade */}
                {reportData.backlogByAge && Object.keys(reportData.backlogByAge).length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">{t.reports.backlogByAge}</h3>
                    <div className="space-y-3">
                      {Object.entries(reportData.backlogByAge).map(([age, count]: [string, any]) => {
                        const totalBacklog = reportData.summary.backlog || reportData.summary.open || 1;
                        const percentage = totalBacklog > 0 ? (count / totalBacklog) * 100 : 0;
                        
                        return (
                          <div key={age} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">{age}</span>
                              <Badge variant="outline">{count}</Badge>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div
                                className="bg-orange-600 h-2.5 rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Métricas de SLA e Transferências */}
                <div className="mt-6 pt-6 border-t grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t.reports.backlogSlaViolations}</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.overdueOpen}</span>
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          {reportData.summary.backlog_overdue || 0}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.passedSla}</span>
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          {reportData.summary.backlog_sla_breached || 0}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Violações no período</span>
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          {reportData.slaBreaches || 0}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t.reports.transferredTicketsCard}</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.totalTransferredShort}</span>
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          {reportData.transfers?.total || 0}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.backlogTransferredShort}</span>
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          {reportData.transfers?.backlog || 0}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.multipleTransfersShort}</span>
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                          {reportData.transfers?.multiple_transfers || 0}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Por Técnico */}
            {Object.keys(reportData.byTechnician || {}).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t.reports.ticketsByTechnician}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(reportData.byTechnician || reportData.topTechnicians || {}).map(([technician, count]: [string, any]) => (
                      <div key={technician} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{technician}</span>
                        <Badge variant="outline">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Chamados Transferidos - Card Dedicado */}
            {(reportData.reportType === 'summary' || reportData.reportType === 'detailed' || !reportData.reportType) && 
             (reportData.transfers?.total > 0 || reportData.summary?.backlog_transferred > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ClockIcon className="h-5 w-5 mr-2" />
                    {t.reports.transferredAnalysis}
                  </CardTitle>
                  <CardDescription>
                    {t.reports.transferredStatsDesc}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t.reports.totalTransferredShort}</p>
                      <p className="text-3xl font-bold text-yellow-700">
                        {reportData.transfers?.total || 0}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {reportData.summary?.total > 0 
                          ? t.reports.ofTotal(parseFloat(((reportData.transfers?.total || 0) / reportData.summary.total * 100).toFixed(1)))
                          : t.reports.ofTotal(0)}
                      </p>
                    </div>
                    
                    <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t.reports.backlogTransferredShort}</p>
                      <p className="text-3xl font-bold text-orange-700">
                        {reportData.transfers?.backlog || reportData.summary?.backlog_transferred || 0}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {reportData.summary?.backlog > 0
                          ? t.reports.ofBacklog(parseFloat(((reportData.transfers?.backlog || reportData.summary?.backlog_transferred || 0) / reportData.summary.backlog * 100).toFixed(1)))
                          : t.reports.ofBacklog(0)}
                      </p>
                    </div>
                    
                    <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t.reports.multipleTransfersShort}</p>
                      <p className="text-3xl font-bold text-red-700">
                        {reportData.transfers?.multiple_transfers || 0}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t.reports.transferredMoreThanOnce}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">{t.reports.distribution}</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.transferredAndResolved}</span>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {(reportData.transfers?.total || 0) - (reportData.transfers?.backlog || reportData.summary?.backlog_transferred || 0)}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.transferredStillOpen}</span>
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          {reportData.transfers?.backlog || reportData.summary?.backlog_transferred || 0}
                        </Badge>
                      </div>
                      {reportData.transfers?.multiple_transfers > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{t.reports.multipleTransfersRate}</span>
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            {reportData.transfers?.total > 0
                              ? `${((reportData.transfers.multiple_transfers / reportData.transfers.total) * 100).toFixed(1)}%`
                              : '0%'}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Métricas de Qualidade - apenas para summary e detailed */}
            {(reportData.reportType === 'summary' || reportData.reportType === 'detailed' || !reportData.reportType) && (
              <>
                {/* Métricas de Qualidade */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">{t.reports.slaQuality}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-green-600">{reportData.summary.sla_compliance}%</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t.reports.violationsInPeriodShort(reportData.slaBreaches || 0)}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Taxa de Reabertura</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-orange-600">{reportData.reopenRate}%</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">NPS Médio</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-blue-600">{reportData.npsAverage || 0}/10</p>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Reports;
